import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { environment } from '@env/environment';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { IdTokenClaims } from '@azure/msal-common';
import {
  captureMsalAuthFailure,
  captureMsalRedirectFailure,
  captureRouteNavigationMetric,
  isMsalFailureEvent,
  setApmUserFromAccount,
} from '@app/core/observability/observability';
import { SharedModule } from '@app/shared/shared.module';

import {
  AppState,
  AuthActionTypes,
  LocationActionTypes,
  selectUserApplicationProfiles,
  selectUserTheme,
} from './core/ngrx';

import { MEDLocations } from '@app/shared/data/locations.data';
import { MSALAuthService } from './auth/msal-auth.service';
import { PortalComponent } from './portal/portal.component';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};
@Component({
  selector: 'ecp-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [SharedModule, PortalComponent],
})
export class AppComponent implements OnInit {
  private static readonly AUTH_ERROR_QUERY_PARAM = 'authError';
  private static readonly ANNOUNCEMENTS_AUTH_ERROR_CODE = 'announcements_auth_failed';
  private static readonly GENERIC_AUTH_ERROR_CODE = 'auth_failed';
  // @select(['auth', 'error'])
  authError$: Observable<any>;
  // @select(['user', 'profile', 'theme'])
  public theme$: Observable<string>;
  // @select(['user', 'profile', 'applicationProfiles'])
  public applicationProfiles$: Observable<any[]>;
  private _myLinkKPILocations = [
    'MED-289',
    'MED-290',
    'MED-291',
    'MED-292',
    'MED-293',
    'MED-294',
    'MED-352',
    'MED-663',
  ];
  private adminLocations = MEDLocations;
  private unsubscribe$ = new Subject<void>();
  // @select(['user', 'pending'])
  loading$: Observable<boolean>;
  // @select(['user', 'error'])
  error$: Observable<any>;
  theme = 'one-portal';
  public isAuthenticated: boolean;
  /**
   * constructor
   * @param ngZone
   * @param {Router} router
   * @param {ActivatedRoute} activatedRoute
   * @param {Title} titleService
   * @param {AuthService} authService
   * @param {Store<AppState>} store
   */
  constructor(
    private ngZone: NgZone,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private authService: MSALAuthService,
    private store: Store<AppState>,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalAuthService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {}

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    this.msalAuthService.initialize().subscribe();
    this.msalAuthService
      .handleRedirectObservable()
      .pipe(
        catchError((error) => {
          this.handleRedirectError(error);
          return EMPTY;
        }),
      )
      .subscribe();
    this.checkLogin();
    this.msalAuthService.instance.enableAccountStorageEvents();
    this.handleMSALAuth();
    this.authError$ = this.store.pipe(select((state) => state.auth.error));
    this.theme$ = this.store.pipe(select(selectUserTheme));
    this.applicationProfiles$ = this.store.pipe(select(selectUserApplicationProfiles));
    this.loading$ = this.store.pipe(select((state) => state.user.pending));
    this.error$ = this.store.pipe(select((state) => state.user.error));
    this.store.pipe(select((state) => state.user)).subscribe((user) => {
      if (user.loaded && user.profile.userId) {
        this.isAuthenticated = true;
      }
    });

    this.listenToNavigationEnd().subscribe((data) => {
      window.scrollTo(0, 0);
      captureRouteNavigationMetric(environment, {
        routeTitle: data?.['title'],
        routePath: this.router.url,
      });
      this.updatePageTitle(data['title']);
    });
    this.applicationProfiles$.subscribe((profiles) => {
      // loop through the applications for the MYLINK app
      if (profiles) {
        // console.log('> profiles => ', profiles);
        profiles.forEach((app) => {
          if (app.application.toLowerCase().includes('med mylink')) {
            // console.log('> app.medLocations[0] => ', app.medLocations[0]);
            if (app.medLocations[0] === 'ALL') {
              // adminLocations
              this.store.dispatch({
                type: LocationActionTypes.LOAD_LOCATION,
                payload: { location: 'MED-190' },
              });
            } else {
              // MED Locations the user has access to:
              // Load the initial MED Location the user has access to
              this.store.dispatch({
                type: LocationActionTypes.LOAD_LOCATION,
                payload: { location: app.medLocations[0] },
              });
            }

            // If the user's locations match the KPI locations we need to redirect them to the KPI page
            // if (
            //   this.findCommonElements3(
            //     this._myLinkKPILocations,
            //     app.medLocations,
            //   )
            // ) {
            //   this.navigateAway('kpi');
            // } else {
            // if (app.medRole === 'EssilorAdmin') {
            //   console.log('yes');
            //   this.navigateAway('essiloradmin/dashboard');
            // } else if (app.medRole === 'User') {
            //   this.navigateAway('orders');
            // }
          }
        });
      }
    });

    // this.authService.getAccessToken().then((token) => {
    //   if (token) this.store.dispatch({ type: AuthActionTypes.LOGIN_SUCCESS });
    // });
  }

  private navigateAway(url) {
    this.ngZone.run(() => this.router.navigateByUrl(url));
  }

  private findCommonElements3(arr1, arr2) {
    return arr1.some((item) => arr2.includes(item));
  }

  /**
   * updatePageTitle
   * @param {string} title
   */
  private updatePageTitle(title: string): void {
    if (!title) return this.titleService.setTitle(`MyLink® Portal`);
    this.titleService.setTitle(`${title} | MyLink® Portal`);
  }

  /**
   * listenToNavigationEnd
   * @returns {Observable<any>}
   */
  private listenToNavigationEnd(): Observable<any> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route: ActivatedRoute) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route) => route.data),
    );
  }

  private handleMSALAuth(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (this.msalAuthService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.checkLogin();
        }
      });
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        this.checkLogin();
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        localStorage.clear();
        this.msalAuthService.logout();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => isMsalFailureEvent(msg.eventType)),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((result: EventMessage) => {
        captureMsalAuthFailure(environment, result);
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS,
        ),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (idtoken.acr === 'B2C_1A_SIGNIN_MEDMYLINK' || idtoken.tfp === 'B2C_1A_SIGNIN_MEDMYLINK') {
          this.msalAuthService.instance.setActiveAccount(payload.account);
        }
        setApmUserFromAccount(environment, this.msalAuthService.instance.getActiveAccount() || payload.account);
      });
  }

  private checkLogin(): void {
    const accounts = this.msalAuthService.instance.getAllAccounts();
    const activeAccount = this.msalAuthService.instance.getActiveAccount() || accounts[0];

    if (activeAccount && this.isAccountSessionValid(activeAccount)) {
      this.store.dispatch({ type: AuthActionTypes.LOGIN_SUCCESS });
    }
  }

  private checkAndSetActiveAccount() {
    const activeAccount = this.msalAuthService.instance.getActiveAccount();

    if (!activeAccount && this.msalAuthService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalAuthService.instance.getAllAccounts();
      this.msalAuthService.instance.setActiveAccount(accounts[0]);
    }
  }

  private isAccountSessionValid(account: any): boolean {
    const exp = Number(account?.idTokenClaims?.exp || 0);
    if (!exp) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    return now < exp;
  }

  private handleRedirectError(error: unknown): void {
    const hashParams = this.getHashAuthErrorParams();
    const currentRoute = this.router.url || window.location.pathname || '';
    const rawErrorMessage =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
        ? error
        : hashParams.errorDescription || hashParams.errorCode || 'MSAL redirect handler error';

    captureMsalRedirectFailure(environment, {
      route: currentRoute,
      errorCode: hashParams.errorCode,
      errorDescription: hashParams.errorDescription,
      rawErrorMessage: rawErrorMessage,
    });

    localStorage.clear();
    this.msalAuthService.instance.setActiveAccount(null);
    this.clearErrorHashFromUrl();

    const authErrorCode = currentRoute.startsWith('/announcements')
      ? AppComponent.ANNOUNCEMENTS_AUTH_ERROR_CODE
      : AppComponent.GENERIC_AUTH_ERROR_CODE;

    this.ngZone.run(() => {
      this.router.navigate(['/login'], {
        queryParams: {
          [AppComponent.AUTH_ERROR_QUERY_PARAM]: authErrorCode,
        },
      });
    });
  }

  private getHashAuthErrorParams(): { errorCode?: string; errorDescription?: string } {
    if (typeof window === 'undefined' || !window.location.hash) {
      return {};
    }

    const hashValue = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hashValue);
    const errorCode = params.get('error') || undefined;
    const errorDescription = params.get('error_description') || undefined;
    return { errorCode, errorDescription };
  }

  private clearErrorHashFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const hashValue = window.location.hash || '';
    if (!hashValue.includes('error=')) {
      return;
    }

    const clearedUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, clearedUrl);
  }
}
